import { useFetchExportedFiles } from '@/hooksApi';
import { Download, FileText } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import { formatDateByDayjs, triggerDownload } from '@/utils';
import { SettingsPageLayout } from '@/components/SettingsPageLayout';
import { SettingsListLoading } from '@/components/SettingsListLoading';

const SettingsFiles = () => {
  const { t } = useTranslation();
  const { data: exportedFileList, isLoading } = useFetchExportedFiles();

  const files = exportedFileList?.object?.items || [];

  return (
    <SettingsPageLayout title='file'>
      <h1 className='text-xl font-semibold text-sd-foreground m-0'>{t('file')}</h1>

      {/* Loading State */}
      {isLoading ? (
        <SettingsListLoading />
      ) : files.length === 0 ? (
        /* Empty State */
        <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm p-8 text-center'>
          <FileText className='h-10 w-10 mx-auto text-sd-muted-foreground mb-3' />
          <p className='text-sm text-sd-muted-foreground'>{t('no_files_yet')}</p>
        </div>
      ) : (
        /* Export Cards */
        <div className='space-y-4'>
          {files.map((item) => {
            const isCompleted = !!item.file;
            const hasExpiration = !!item.expire_at;

            return (
              <div
                key={item.uid}
                className='rounded-2xl border border-sd-border bg-sd-card shadow-sm'
              >
                <div className='p-5 space-y-4'>
                  {/* Header row */}
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center gap-3'>
                      <div className='h-10 w-10 rounded-lg bg-sd-muted flex items-center justify-center shrink-0'>
                        <FileText className='h-5 w-5 text-sd-muted-foreground' />
                      </div>
                      <h3 className='text-sm font-semibold text-sd-foreground'>
                        {t('bulk_export')}
                      </h3>
                    </div>
                    {isCompleted ? (
                      <span className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-xs font-semibold text-emerald-700'>
                        <span className='h-1.5 w-1.5 rounded-full bg-emerald-500' />
                        {t('completed')}
                      </span>
                    ) : (
                      <span className='inline-flex items-center gap-2 text-sm text-sd-muted-foreground'>
                        <svg
                          className='animate-spin h-4 w-4'
                          xmlns='http://www.w3.org/2000/svg'
                          fill='none'
                          viewBox='0 0 24 24'
                        >
                          <circle
                            className='opacity-25'
                            cx='12'
                            cy='12'
                            r='10'
                            stroke='currentColor'
                            strokeWidth='4'
                          />
                          <path
                            className='opacity-75'
                            fill='currentColor'
                            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                          />
                        </svg>
                        {t('in_queue')}
                      </span>
                    )}
                  </div>

                  <div className='border-t border-sd-border' />

                  {/* Requested Date */}
                  <div className='flex items-center justify-between'>
                    <span className='text-sm text-sd-muted-foreground'>{t('requested_date')}</span>
                    <span className='text-sm font-medium text-sd-foreground'>
                      {formatDateByDayjs(item.created_at, 'D MMM YYYY, HH:mm')}
                    </span>
                  </div>

                  <div className='border-t border-sd-border' />

                  {/* File name */}
                  <div className='space-y-1.5'>
                    <p className='text-xs font-medium text-sd-muted-foreground uppercase tracking-wider'>
                      {t('export_file')}
                    </p>
                    <p className='text-sm text-sd-foreground break-all'>{item.name}</p>
                  </div>

                  {/* Expiry notice */}
                  {hasExpiration && (
                    <div className='rounded-lg bg-amber-50 border border-amber-200 px-4 py-3'>
                      <p className='text-sm text-amber-800'>
                        <Trans
                          i18nKey='file_expiration_warning'
                          values={{
                            date: formatDateByDayjs(item.expire_at, 'D MMM YYYY, HH:mm A'),
                          }}
                          components={{ 1: <span className='font-semibold' /> }}
                        />
                      </p>
                    </div>
                  )}

                  {/* Download button */}
                  {isCompleted && (
                    <button
                      onClick={() => {
                        triggerDownload(item.file.abs_url, '');
                      }}
                      className='flex items-center justify-center gap-2 w-full py-2.5 rounded-lg bg-sd-primary text-sd-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity'
                    >
                      <Download className='h-4 w-4' />
                      {t('download').toUpperCase()}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SettingsPageLayout>
  );
};

export default SettingsFiles;
